import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Fade,
  Zoom,
  useTheme
} from '@mui/material';
import MicrosoftIcon from '@mui/icons-material/AccountBox'; // Replace with actual Microsoft icon if available
import AuthService from '../services/auth';

const Login = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();

  // Redirect to home if already authenticated
  useEffect(() => {
    let isMounted = true;
    const check = async () => {
      try {
        const ok = await AuthService.isAuthenticated();
        if (isMounted && ok) {
          navigate('/', { replace: true });
        }
      } catch (e) {
        // ignore, stay on login
      }
    };
    check();
  }, [navigate]);

  // Animation effect when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationComplete(true);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Handle Microsoft login
  const handleMicrosoftLogin = () => {
    setLoading(true);
    // Спрямовуємо запит до бекенду через публічний префікс "/api"
    const baseUrl = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;
    window.location.href = `${baseUrl}/oauth2/authorization/microsoft`;
  };

  return (
    <Container maxWidth="sm">
      <Fade in={true} timeout={800}>
        <Box sx={{ 
          mt: { xs: 4, sm: 8 }, 
          mb: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <Zoom in={true} timeout={700}>
            <Paper 
              elevation={6} 
              sx={{ 
                p: { xs: 3, sm: 4 },
                width: '100%',
                borderRadius: 3,
                overflow: 'hidden',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: 'linear-gradient(90deg, #da0606 0%, #b00000 100%)',
                }
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                mb: 3
              }}>
                <Typography 
                  variant="h4" 
                  component="h1" 
                  align="center" 
                  sx={{ 
                    fontWeight: 'bold',
                    color: 'primary.main',
                    mb: 1
                  }}
                >
                  Вхід до системи
                </Typography>
                <Typography 
                  variant="body1" 
                  color="text.secondary" 
                  align="center"
                  sx={{ maxWidth: 400 }}
                >
                  Увійдіть, щоб отримати доступ до бронювання інвентарю
                </Typography>
              </Box>

              {error && (
                <Zoom in={true} timeout={500}>
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mb: 3,
                      borderRadius: 2,
                      '& .MuiAlert-icon': {
                        color: theme.palette.error.main
                      }
                    }}
                  >
                    {error}
                  </Alert>
                </Zoom>
              )}

              <Box sx={{ mt: 4, mb: 2 }}>
                <Fade in={animationComplete} timeout={500} style={{ transitionDelay: '200ms' }}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleMicrosoftLogin}
                    startIcon={<MicrosoftIcon />}
                    disabled={loading}
                    sx={{ 
                      py: 2,
                      borderRadius: 8,
                      fontWeight: 500,
                      fontSize: '1.1rem',
                      boxShadow: 'none',
                      backgroundColor: theme.palette.primary.main,
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                        boxShadow: '0px 6px 12px rgba(218, 6, 6, 0.2)',
                        transform: 'translateY(-2px)',
                        transition: 'all 0.3s ease'
                      }
                    }}
                  >
                    {loading ? 'Вхід...' : 'Увійти через Microsoft'}
                  </Button>
                </Fade>
              </Box>
            </Paper>
          </Zoom>
        </Box>
      </Fade>
    </Container>
  );
};

export default Login;
